import { throttle } from "lodash";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/description";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import router from "@/router";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "../composables/draft-manager";
import { useEditManager } from "../composables/edit-manager";

export type Event = types.ProcessEvent;

// Uses the current route to determine what the 'event' should be and fetches
// the event description for you.
export const useEventStore = defineEntityStore("event", () => {
    const event = ref<Event>();
    const syncStore = useSyncStore();
    const entityId = computed(() => event.value?.identity);
    const description = ref<Artefact<"text/markdown">>();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.EVENT,
        attribute: "description",
        artefact: description,
    });
    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.EVENT);

    const {
        organization,
        stek,
        process,
        event: eventInfo,
    } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const revisionQuery = computed(() =>
        releaseStore.activeRevision
            ? new URLSearchParams({ revision: releaseStore.activeRevision })
            : undefined
    );

    const isFetching = ref(false);
    const controller = ref(new AbortController());

    async function updateEvent(force = false) {
        const eventId = eventInfo.value?.identity;

        // In case the same event is selected, return early.
        if (!force && event.value && event.value.identity === eventId) {
            return;
        }

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!eventId) {
            description.value = undefined;
            event.value = undefined;
            return;
        }

        isFetching.value = true;
        description.value = undefined;
        event.value = undefined;

        const { response, data } =
            await api.startProcessProcessElementMgtGetEventStartPost({
                body: {
                    event_id: `${eventId}`,
                    include_archived: true,
                    revision: releaseStore.activeRevision ?? undefined,
                },
                signal: controller.value.signal,
            });

        if (!response.ok) {
            if (response.status === 404) {
                if (!releaseStore.activeRevision) {
                    router.replace({
                        name: "not-found",
                        params: {
                            orgName: organization.value?.name,
                            stekName: stek.value?.name,
                        },
                        query: {
                            type: "event",
                            id: eventId,
                        },
                    });
                }
                return;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        event.value = data;
        isFetching.value = false;

        if (event.value) {
            await updateDescription(eventId);
        }
    }
    watch(
        () => eventInfo.value?.identity,
        () => updateEvent(),
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (event.value) updateEvent(true);
        }
    );

    const updateDescription = async (currentEventId?: string | undefined) => {
        if (!event.value) {
            return;
        }

        if (!description.value) {
            try {
                const descriptionResult = await getDescriptionArtefact(
                    event.value,
                    "eventDescription",
                    controller.value.signal,
                    revisionQuery.value
                );

                // Check if event has changed during the async operation
                if (!event.value || event.value.identity !== currentEventId) {
                    console.debug(
                        "Event changed during description fetch, aborting description update"
                    );
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            }
        }
    };

    const isModified = computed(() => {
        if (isFetching.value) {
            return false;
        }

        return !!description.value?.isModified;
    });

    const queueSave = async () => {
        if (!isModified.value || !event.value) {
            return;
        }

        const updateContent: types.StartProcessProcessElementMgtUpdateEventStartPostData =
            {
                body: {
                    event_id: event.value.identity,
                },
            };

        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (
            !updateContent.body.event_id ||
            Object.keys(updateContent.body).length === 1
        ) {
            // Only event_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.EVENT,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.EVENT &&
                identity.entityId === event.value?.identity
            ) {
                if (stek.value) {
                    updateEvent(true);
                }
            }
        }
    );

    const deleteEvent = async () => {
        if (!event.value) {
            return;
        }
        if (!event.value.is_archived) {
            throw Error("Can only delete archived events");
        }

        await api.startProcessProcessElementMgtDeleteEventStartPost({
            body: {
                event_id: event.value.identity,
            },
        });

        if (organization.value && stek.value && process.value) {
            await router.push({
                name: "processPage",
                params: {
                    orgName: organization.value.name,
                    stekName: stek.value.name,
                    processId: process.value.identity,
                },
            });
        }
    };

    return {
        event,
        description,
        isModified,
        isFetching,
        reset,
        deleteEvent,
    };
});

export const useEventManager = () => storeToRefs(useEventStore());
